import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { MANIFESTO, SERVICES, PROCESS, CASES, TESTIMONIALS, IMAGES } from "@/data";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div variants={reveal} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className={className}>
      {children}
    </motion.div>
  );
}

export function Overline({ children }) {
  return <span className="font-body text-xs font-bold uppercase tracking-[0.25em] text-black/50">{children}</span>;
}

export function EditorialMarquee() {
  const items = ["Performance Marketing", "Amazon Ads", "Creative That Sells", "CRO", "Retention", "Agentic AI", "D2C Growth"];
  return (
    <section className="py-8 border-y border-black/10 bg-[#0A0A0A]" data-testid="marquee">
      <Marquee speed={40} gradient={false} autoFill>
        {items.map((t, idx) => (
          <div key={idx} className="flex items-center">
            <span className="font-display text-3xl md:text-5xl font-bold tracking-tight text-[#FDFCF8] px-8">{t}</span>
            <span className="h-3 w-3 rounded-full" style={{ background: ["#F48220", "#E70D71", "#3A53A3"][idx % 3] }} />
          </div>
        ))}
      </Marquee>
    </section>
  );
}

export function Manifesto() {
  return (
    <section className="bg-[#050505] text-[#FDFCF8] py-24 md:py-36" data-testid="manifesto">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal><Overline><span className="text-white/50">The Uncomfortable Truth</span></Overline></Reveal>
        <Reveal i={1}>
          <h2 className="mt-6 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[18ch]">
            Growth hides the leaks. We drag them into the light.
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-12">
          {MANIFESTO.map((m, idx) => (
            <Reveal i={idx} key={m.n} className="border-t border-white/15 pt-6">
              <div className="flex items-start gap-6">
                <span className="font-display text-lg font-bold text-[#F48220]">{m.n}</span>
                <p className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-snug">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Services() {
  const spans = ["md:col-span-2", "", "", "md:col-span-2", "md:col-span-2", "", "", "md:col-span-2"];
  return (
    <section id="services" className="py-24 md:py-36" data-testid="services">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Overline>What We Do</Overline></Reveal>
            <Reveal i={1}>
              <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[14ch]">
                One team. The whole growth engine.
              </h2>
            </Reveal>
          </div>
          <Reveal i={2}>
            <p className="font-body text-black/60 max-w-[36ch] leading-relaxed">
              From the first impression to the repeat purchase — media, creative, pages and automation built to compound.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {SERVICES.map((s, idx) => (
            <Reveal i={idx % 3} key={s.n} className={spans[idx]}>
              <div className="group h-full rounded-2xl border border-black/10 bg-white p-8 hover-lift hover:border-[#F48220]/40">
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-bold text-[#3A53A3]">{s.n}</span>
                  <span className="h-2.5 w-2.5 rounded-full bg-black/10 group-hover:bg-[#E70D71] transition-colors duration-300" />
                </div>
                <h3 className="mt-8 font-display text-2xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 font-body text-sm text-black/60 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Process() {
  return (
    <section id="process" className="py-24 md:py-36 bg-white border-y border-black/10" data-testid="process">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal><Overline>How The Free Audit Works</Overline></Reveal>
        <Reveal i={1}>
          <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[16ch]">
            Four steps to see where the money really goes.
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-4 gap-8">
          {PROCESS.map((p, idx) => (
            <Reveal i={idx} key={p.n}>
              <div className="relative">
                <span className="font-display text-6xl font-extrabold tracking-tighter" style={{ color: ["#F48220", "#E70D71", "#3A53A3", "#0A0A0A"][idx] }}>{p.n}</span>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{p.title}</h3>
                <p className="mt-2 font-body text-sm text-black/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CaseStudies() {
  return (
    <section id="results" className="py-24 md:py-36" data-testid="results">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Overline>Proof, Not Promises</Overline></Reveal>
            <Reveal i={1}>
              <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[14ch]">
                Numbers we're happy to be judged by.
              </h2>
            </Reveal>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {CASES.map((c, idx) => (
            <Reveal i={idx % 3} key={c.client}>
              <div className="group h-full rounded-2xl border border-black/10 bg-white p-8 hover-lift overflow-hidden relative">
                <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500" style={{ background: c.color }} />
                <div className="font-display text-sm font-bold tracking-wide uppercase text-black/50">{c.client}</div>
                <div className="mt-4 font-display text-4xl md:text-5xl font-extrabold tracking-tighter" style={{ color: c.color }}>{c.metric}</div>
                <p className="mt-4 font-body text-sm text-black/60 leading-relaxed">{c.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section className="py-24 md:py-36 bg-[#050505] text-[#FDFCF8]" data-testid="testimonials">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal><Overline><span className="text-white/50">Founders On Record</span></Overline></Reveal>
        <Reveal i={1}>
          <h2 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-[16ch]">
            The kind of results founders remember.
          </h2>
        </Reveal>
        <div className="mt-16 columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {TESTIMONIALS.map((t, idx) => (
            <Reveal i={idx % 3} key={t.name} className="mb-6 break-inside-avoid">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                <div className="flex gap-1 mb-4">
                  {[0, 1, 2].map((k) => (
                    <span key={k} className="h-2 w-2 rounded-full" style={{ background: ["#F48220", "#E70D71", "#3A53A3"][k] }} />
                  ))}
                </div>
                <p className="font-body text-base leading-relaxed text-white/85">"{t.quote}"</p>
                <div className="mt-6">
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="font-body text-sm text-white/50">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal i={2} className="mb-6 break-inside-avoid">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.team} alt="DNA Digital team" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
